import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Paper,
  Grid,
  Typography,
  Alert,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ContainedButton } from "../../Common/Buttons";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  ACCREDITATION_TYPES,
  validateFile,
  formatFileSize,
} from "./utils";
import { ACCREDITEDKYCWEBSERVICE, showToast } from "../../CoreDataService";

const AccreditedDocumentUpload = ({
  handleNext,
  handleBack,
  userDetails,
  formData,
  handleFormDataUpdate,
  theme,
  setIsLoading: setParentIsLoading,
  onSubmitSuccess,
  isRejected
}) => {
  const { btncolor } = useSelector((state) => state);
  const accreditationType = formData?.accreditationType;
  const applicationId = formData?.applicationId;
  const token = localStorage.getItem("access_token");
  const userId = userDetails?.user_id || userDetails?.userId || userDetails?.id;

  // Dark mode colors
  const darkColors = {
    background: "#121212",
    paper: "#1e1e1e",
    card: "#1a1a1a",
    text: "#e0e0e0",
    textSecondary: "#aaa",
    border: "#333",
    primary: "#0d6efd",
    success: "#28a745",
    info: "#0dcaf0",
    warning: "#ffc107",
    error: "#dc3545",
  };

  // Light mode colors
  const lightColors = {
    background: "#f8f9fa",
    paper: "#ffffff",
    card: "#f8f9fa",
    text: "#212529",
    textSecondary: "#6c757d",
    border: "#dee2e6",
    primary: "#1976d2",
    success: "#2e7d32",
    info: "#0288d1",
    warning: "#ed6c02",
    error: "#d32f2f",
  };

  const colors = theme === "dark" ? darkColors : lightColors;

  // Store selected files locally (not uploaded yet)
  const [selectedFiles, setSelectedFiles] = useState(formData?.selectedFiles || {});
  const [uploadedFileIds, setUploadedFileIds] = useState(formData?.uploadedFileIds || {});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [documentRequirements, setDocumentRequirements] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [uploadResults, setUploadResults] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  // Fetch document requirements on component mount
  useEffect(() => {
    if (accreditationType && applicationId) {
      fetchDocumentRequirements();
    }
  }, [accreditationType, applicationId]);

  const fetchDocumentRequirements = async () => {
    setIsLoading(true);
    setApiError(null);

    try {
      const response = await axios.get(
        `${ACCREDITEDKYCWEBSERVICE}accreditation/document-requirements/${accreditationType}`,
        {
          headers: {
            Authorization: `BEARER ${token}`,
          },
        }
      );

      if (response?.data?.success) {
        const data = response.data.data;

        const groupDocCodes = accreditationType === "INCOME"
          ? ["TAX_RETURN", "CPA_LETTER"]
          : [];

        const requiredGroups = accreditationType === "INCOME"
          ? [{
            groupId: "primary",
            label: "Tax Return or CPA Verification Letter",
            hint: "At least one of these must be provided",
            codes: ["TAX_RETURN", "CPA_LETTER"],
          }]
          : [];

        const transformedRequirements = {
          documents: data.allowedDocumentTypes.map(doc => ({
            code: doc.code,
            label: doc.description,
            required: doc.required && !groupDocCodes.includes(doc.code),
            requiredGroup: groupDocCodes.includes(doc.code) ? "primary" : null,
            hint: getDocumentHint(doc.code, accreditationType),
          })),
          minimumDocuments: data.minimumDocuments,
          description: data.description,
          accreditationType: data.accreditationType,
          accreditationTypeDescription: data.accreditationTypeDescription,
          requiredGroups,
        };

        setDocumentRequirements(transformedRequirements);
      } else {
        throw new Error(response?.data?.message || "Failed to fetch document requirements");
      }
    } catch (error) {
      console.error("Error fetching document requirements:", error);
      setApiError(error.response?.data?.message || error.message || "Failed to fetch document requirements");
      showToast("error", "Failed to fetch document requirements");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to generate hints for document types
  const getDocumentHint = (docCode, accType) => {
    const hints = {
      TAX_RETURN: "Tax returns for the last 2 years",
      W2_FORM: "W-2 forms for the last 2 years",
      "FORM_1099": "1099 forms showing investment income",
      CPA_LETTER: "Letter from CPA verifying income",
      EMPLOYER_LETTER: "Employment verification letter",
      THIRD_PARTY_VERIFICATION_LETTER: "Third-party verification letter",
      OTHER: "Any other supporting documents",
    };

    return hints[docCode] || `Upload ${docCode.replace(/_/g, ' ').toLowerCase()}`;
  };

  // Handle file selection WITHOUT uploading
  const handleFileChange = (docCode, required) => (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const file = fileList[0];
    const validationResult = validateFile(fileList, required);

    if (validationResult !== true) {
      showToast("error", validationResult);

      // Reset the input
      e.target.value = '';
      setSelectedFiles(prev => {
        const updated = { ...prev };
        delete updated[docCode];
        return updated;
      });
      return;
    }

    // Store the file locally
    setSelectedFiles(prev => ({
      ...prev,
      [docCode]: file
    }));
  };

  // Remove selected file
  const handleRemoveFile = (docCode) => {
    setSelectedFiles((prev) => {
      const updated = { ...prev };
      delete updated[docCode];
      return updated;
    });

    setUploadedFileIds((prev) => {
      const updated = { ...prev };
      delete updated[docCode];
      return updated;
    });

    // Reset the file input
    const input = document.getElementById(`file-input-${docCode}`);
    if (input) input.value = "";
  };

  // Check if minimum docs are met
  const getUploadStatus = () => {
    if (!documentRequirements) return { valid: false, uploadedCount: 0, minRequired: 0 };

    const totalUploadedCount = Object.keys(selectedFiles).length;

    // Hard-required docs (required: true, no group)
    const hardRequired = documentRequirements.documents.filter(
      (d) => d.required && !d.requiredGroup
    );
    const allHardRequiredUploaded = hardRequired.every((d) => selectedFiles[d.code]);

    // Group-required: at least one from each group must be uploaded
    const groups = documentRequirements.requiredGroups || [];
    const allGroupsSatisfied = groups.every((group) =>
      group.codes.some((code) => selectedFiles[code])
    );

    const hasMinimumTotal = totalUploadedCount >= documentRequirements.minimumDocuments;
    const isValid = allHardRequiredUploaded && allGroupsSatisfied && hasMinimumTotal;

    const missingGroups = groups
      .filter((group) => !group.codes.some((code) => selectedFiles[code]))
      .map((group) => group.label);

    return {
      valid: isValid,
      uploadedCount: totalUploadedCount,
      minRequired: documentRequirements.minimumDocuments,
      allHardRequiredUploaded,
      allGroupsSatisfied,
      missingGroups,
      hasUploadingFiles: Object.keys(uploadingFiles).length > 0,
    };
  };

  const uploadStatus = getUploadStatus();

  // Function to upload a single document
  const uploadDocument = async (docCode, file) => {
    if (!file || !applicationId || !userId) {
      throw new Error("Missing required information for upload");
    }

    const formData = new FormData();
    formData.append("applicationId", applicationId);
    formData.append("userId", userId);
    formData.append("documentType", docCode);
    formData.append("document", file);

    try {
      const response = await axios.post(
        `${ACCREDITEDKYCWEBSERVICE}accreditation/documents/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `BEARER ${token}`,
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress((prev) => ({
              ...prev,
              [docCode]: progress,
            }));
          },
        }
      );

      if (response?.data?.success) {
        return {
          success: true,
          data: response.data.data,
          docCode,
          fileName: file.name,
        };
      } else {
        throw new Error(response?.data?.message || "Upload failed");
      }
    } catch (error) {
      console.error(`Error uploading document ${docCode}:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || "Upload failed",
        docCode,
        fileName: file.name,
      };
    }
  };

  // Handle form submission - Upload all files and then proceed
  const onSubmit = async () => {
    if (!uploadStatus.valid) {
      if (!uploadStatus.allGroupsSatisfied) {
        showToast("warning", `Please upload at least one of: ${uploadStatus.missingGroups?.join(", ")}`);
      } else {
        showToast("warning", `Please select at least ${uploadStatus.minRequired} document(s) total`);
      }
      return;
    }

    setIsSubmitting(true);
    setApiError(null);
    setUploadResults([]);

    try {
      // Start uploading all selected files
      const uploadPromises = Object.keys(selectedFiles).map(async (docCode) => {
        const file = selectedFiles[docCode];

        // Mark as uploading
        setUploadingFiles((prev) => ({
          ...prev,
          [docCode]: true,
        }));

        // Upload the document
        const result = await uploadDocument(docCode, file);

        // Remove from uploading state
        setUploadingFiles((prev) => {
          const updated = { ...prev };
          delete updated[docCode];
          return updated;
        });

        return result;
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);
      setUploadResults(results);

      // Check if any uploads failed
      const failedUploads = results.filter((result) => !result.success);
      if (failedUploads.length > 0) {
        const errorMessages = failedUploads.map(f => `${f.docCode}: ${f.error}`).join(", ");
        throw new Error(`Some documents failed to upload: ${errorMessages}`);
      }

      // All uploads successful - create mapping of docCode to documentId
      const uploadedIds = {};
      results.forEach((result) => {
        if (result.success && result.data) {
          uploadedIds[result.docCode] = result.data.documentId;
        }
      });

      // Update form data with uploaded file IDs
      handleFormDataUpdate({
        selectedFiles: selectedFiles,
        uploadedFileIds: uploadedIds,
        documentRequirements: documentRequirements,
        hasValidDocuments: true,
        uploadResults: results
      });

      // Show success message
      showToast("success", "All documents uploaded successfully!");

      // Call parent success handler if provided
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }

      // Proceed to next step
      handleNext();
    } catch (error) {
      console.error("Error uploading documents:", error);
      setApiError(error.message || "Failed to upload documents");
      showToast("error", `Failed to upload documents: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!accreditationType || !applicationId) {
    return (
      <Paper sx={{
        p: 3,
        backgroundColor: colors.paper,
        color: colors.text,
        borderRadius: 2,
        boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Alert severity="warning">
          Application information missing. Please go back and complete the previous steps.
        </Alert>
        <div style={{ marginTop: "24px" }}>
          <ContainedButton
            variant="contained"
            onClick={handleBack}
            sx={{
              borderRadius: "1.25rem",
              padding: "10px 25px",
              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: theme === 'dark' ? '#0b5ed7' : '#1565c0'
              }
            }}
          >
            Back
          </ContainedButton>
        </div>
      </Paper>
    );
  }

  if (isLoading) {
    return (
      <Paper sx={{
        p: 3,
        backgroundColor: colors.paper,
        color: colors.text,
        borderRadius: 2,
        boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress sx={{ color: colors.primary, mb: 2 }} />
          <Typography variant="body1" sx={{ color: colors.text }}>
            Loading document requirements...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (apiError) {
    return (
      <Paper sx={{
        p: 3,
        backgroundColor: colors.paper,
        color: colors.text,
        borderRadius: 2,
        boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Alert severity="error" sx={{ mb: 2, backgroundColor: colors.error + (theme === 'dark' ? '20' : '10') }}>
          {apiError}
        </Alert>
        <div style={{ marginTop: "24px" }}>
          <ContainedButton
            variant="contained"
            onClick={handleBack}
            sx={{
              borderRadius: "1.25rem",
              padding: "10px 25px",
              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: theme === 'dark' ? '#0b5ed7' : '#1565c0'
              }
            }}
          >
            Back
          </ContainedButton>
        </div>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: colors.paper,
        color: colors.text,
        borderRadius: 2,
        boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ color: colors.text, fontWeight: 600 }}>
        {ACCREDITATION_TYPES[accreditationType]?.icon || "📄"} Document Upload
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: colors.textSecondary }}>
        Upload the required documents to verify your{" "}
        {ACCREDITATION_TYPES[accreditationType]?.shortLabel || ""} accredited
        investor status.
      </Typography>

      {/* Application ID Info */}
      {applicationId && (
        <Alert severity="info" sx={{
          mb: 2,
          backgroundColor: colors.info + (theme === 'dark' ? '20' : '10')
        }}>
          <Typography variant="body2" sx={{ color: colors.text }}>
            <strong>Application ID:</strong> {applicationId}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mt: 0.5 }}>
            Select all required documents to proceed
          </Typography>
        </Alert>
      )}

      {/* Document Requirements Description */}
      {documentRequirements?.description && (
        <Alert severity="info" sx={{
          mb: 3,
          backgroundColor: colors.info + (theme === 'dark' ? '20' : '10')
        }}>
          <Typography variant="body2" sx={{ color: colors.text }}>
            <strong>Requirements:</strong> {documentRequirements.description}
          </Typography>
        </Alert>
      )}

      {/* Upload progress indicator */}
      <Box
        sx={{
          mb: 3,
          p: 1.5,
          borderRadius: 1,
          backgroundColor: uploadStatus.valid
            ? colors.success + (theme === 'dark' ? '20' : '10')
            : colors.warning + (theme === 'dark' ? '20' : '10'),
          border: `1px solid ${uploadStatus.valid ? colors.success + '30' : colors.warning + '30'}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ color: colors.text, fontWeight: 'bold' }}>
            {uploadStatus.uploadedCount} / {uploadStatus.minRequired} documents selected
          </Typography>
          {uploadStatus.valid ? (
            <Typography variant="caption" sx={{ color: colors.success, display: 'block' }}>
              ✓ All requirements met
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: colors.warning, display: 'block' }}>
              {!uploadStatus.allGroupsSatisfied
                ? `Required: upload at least one of — ${uploadStatus.missingGroups?.join(", ")}`
                : `Need ${uploadStatus.minRequired - uploadStatus.uploadedCount} more document(s)`
              }
            </Typography>
          )}
        </Box>

        {!uploadStatus.allGroupsSatisfied && uploadStatus.missingGroups?.length > 0 && (
          <Typography variant="caption" sx={{
            color: colors.warning,
            backgroundColor: colors.warning + (theme === 'dark' ? '20' : '10'),
            padding: '2px 8px',
            borderRadius: '4px'
          }}>
            Missing: {uploadStatus.missingGroups.join(", ")}
          </Typography>
        )}
      </Box>

      {/* Show upload progress for individual files */}
      {Object.keys(uploadingFiles).length > 0 && (
        <Alert severity="info" sx={{ mb: 3, backgroundColor: colors.info + (theme === 'dark' ? '20' : '10') }}>
          <Typography variant="body2" sx={{ color: colors.text, mb: 1 }}>
            Uploading documents...
          </Typography>
          {Object.keys(uploadingFiles).map((docCode) => (
            <Box key={docCode} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: colors.text, display: 'block' }}>
                {documentRequirements?.documents?.find(d => d.code === docCode)?.label || docCode}: {uploadProgress[docCode] || 0}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress[docCode] || 0}
                sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
              />
            </Box>
          ))}
        </Alert>
      )}

      {/* Show upload results if available */}
      {uploadResults.length > 0 && !isSubmitting && (
        <Alert severity={uploadResults.every(r => r.success) ? "success" : "warning"} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Upload Results:
          </Typography>
          {uploadResults.map((result, index) => (
            <Typography key={index} variant="caption" sx={{ display: 'block', color: result.success ? colors.success : colors.error }}>
              {result.success ? '✓' : '✗'} {result.fileName}: {result.success ? 'Uploaded successfully' : result.error}
            </Typography>
          ))}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>

          {/* ── Required Group Section: Tax Return OR CPA Letter ── */}
          {documentRequirements?.requiredGroups?.length > 0 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text, mb: 0.5 }}>
                  Required Documents{" "}
                  <Typography component="span" variant="caption" sx={{ color: colors.textSecondary }}>
                    (at least one must be uploaded)
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                  Please upload at least one of the following documents
                </Typography>
              </Grid>

              {documentRequirements.requiredGroups.map((group) =>
                documentRequirements.documents
                  .filter((d) => d.requiredGroup === group.groupId)
                  .map((doc) => (
                    <Grid size={{ xs: 12, md: 6 }} key={doc.code}>
                      <Box sx={{
                        border: "1px solid",
                        borderColor: selectedFiles[doc.code]
                          ? colors.success + "50"
                          : !uploadStatus.allGroupsSatisfied ? colors.warning + "50" : colors.border,
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: selectedFiles[doc.code]
                          ? colors.success + (theme === "dark" ? "10" : "05")
                          : colors.card,
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text }}>
                            {doc.label}
                          </Typography>
                          <Chip
                            label="Required (any one)"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              backgroundColor: colors.warning,
                              color: "#fff",
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ display: "block", mb: 2, color: colors.textSecondary }}>
                          {doc.hint}
                        </Typography>
                        {selectedFiles[doc.code] ? (
                          <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            backgroundColor: colors.success + (theme === "dark" ? "10" : "05"),
                            padding: "8px 12px",
                            borderRadius: "4px",
                          }}>
                            <Box>
                              <Typography variant="body2" sx={{ color: colors.success, fontWeight: "bold" }}>
                                {uploadingFiles[doc.code] ? '📤 Uploading...' : '✓ Selected'}
                                {uploadProgress[doc.code] && ` (${uploadProgress[doc.code]}%)`}
                              </Typography>
                              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                {selectedFiles[doc.code].name} ({formatFileSize(selectedFiles[doc.code].size)})
                              </Typography>
                              {uploadProgress[doc.code] > 0 && uploadingFiles[doc.code] && (
                                <LinearProgress
                                  variant="determinate"
                                  value={uploadProgress[doc.code]}
                                  sx={{ mt: 1, height: 4, borderRadius: 2 }}
                                />
                              )}
                            </Box>
                            {!uploadingFiles[doc.code] && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(doc.code)}
                                sx={{ color: colors.text }}
                                disabled={isSubmitting}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ) : (
                          <>
                            <input
                              id={`file-input-${doc.code}`}
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.png,.jfif,.heic"
                              onChange={handleFileChange(doc.code, false)}
                              disabled={isSubmitting}
                              style={{
                                width: "100%",
                                padding: "8px",
                                fontSize: "14px",
                                backgroundColor: theme === "dark" ? colors.card : "#fff",
                                color: colors.text,
                                border: `1px solid ${!uploadStatus.allGroupsSatisfied ? colors.warning : colors.border}`,
                                borderRadius: "4px",
                                outline: "none",
                              }}
                            />
                            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: colors.textSecondary, fontSize: "0.7rem" }}>
                              📄 Allowed: PDF, DOC, DOCX, JPG, PNG, JFIF, HEIC | 📏 Max size: 6 MB
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                  ))
              )}
            </>
          )}

          {/* ── Hard Required Documents (required: true, no group) ── */}
          {documentRequirements?.documents?.filter((d) => d.required && !d.requiredGroup).length > 0 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text, mb: 1 }}>
                  Required Documents
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                  These documents are mandatory for verification
                </Typography>
              </Grid>

              {documentRequirements.documents
                .filter((d) => d.required && !d.requiredGroup)
                .map((doc) => (
                  <Grid size={{ xs: 12, md: 6 }} key={doc.code}>
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: selectedFiles[doc.code]
                          ? colors.success + '50'
                          : colors.error + '50',
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: selectedFiles[doc.code]
                          ? colors.success + (theme === 'dark' ? '10' : '05')
                          : colors.card,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text }}>
                          {doc.label}
                        </Typography>
                        <Chip
                          label="Required"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            backgroundColor: colors.error,
                            color: '#fff'
                          }}
                        />
                      </Box>

                      <Typography variant="caption" sx={{ display: "block", mb: 2, color: colors.textSecondary }}>
                        {doc.hint}
                      </Typography>

                      {selectedFiles[doc.code] ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            backgroundColor: colors.success + (theme === 'dark' ? '10' : '05'),
                            padding: '8px 12px',
                            borderRadius: '4px',
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ color: colors.success, fontWeight: 'bold' }}>
                              {uploadingFiles[doc.code] ? '📤 Uploading...' : '✓ Selected'}
                              {uploadProgress[doc.code] && ` (${uploadProgress[doc.code]}%)`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              {selectedFiles[doc.code].name} ({formatFileSize(selectedFiles[doc.code].size)})
                            </Typography>
                            {uploadProgress[doc.code] > 0 && uploadingFiles[doc.code] && (
                              <LinearProgress
                                variant="determinate"
                                value={uploadProgress[doc.code]}
                                sx={{ mt: 1, height: 4, borderRadius: 2 }}
                              />
                            )}
                          </Box>
                          {!uploadingFiles[doc.code] && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveFile(doc.code)}
                              sx={{ color: colors.text }}
                              disabled={isSubmitting}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ) : (
                        <>
                          <input
                            id={`file-input-${doc.code}`}
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.png,.jfif,.heic"
                            onChange={handleFileChange(doc.code, true)}
                            disabled={isSubmitting}
                            style={{
                              width: "100%",
                              padding: "8px",
                              fontSize: "14px",
                              backgroundColor: theme === 'dark' ? colors.card : '#fff',
                              color: colors.text,
                              border: `1px solid ${doc.required && !selectedFiles[doc.code]
                                ? colors.error
                                : colors.border
                                }`,
                              borderRadius: "4px",
                              outline: 'none',
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mt: 0.5, color: colors.textSecondary, fontSize: "0.7rem" }}
                          >
                            📄 Allowed: PDF, DOC, DOCX, JPG, PNG, JFIF, HEIC | 📏 Max size: 6 MB
                          </Typography>
                          {doc.required && !selectedFiles[doc.code] && (
                            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                              {doc.label} is required
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Grid>
                ))}
            </>
          )}

          {/* ── Optional Supporting Documents ── */}
          {documentRequirements?.documents?.filter((d) => !d.required && !d.requiredGroup).length > 0 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: colors.text, mt: 3, mb: 1 }}
                >
                  Optional Supporting Documents
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ display: "block", mb: 2, color: colors.textSecondary }}
                >
                  Additional documents can strengthen your application and speed
                  up the review process.
                </Typography>
              </Grid>

              {documentRequirements.documents
                .filter((d) => !d.required && !d.requiredGroup)
                .map((doc) => (
                  <Grid size={{ xs: 12, md: 6 }} key={doc.code}>
                    <Box
                      sx={{
                        border: "1px dashed",
                        borderColor: selectedFiles[doc.code]
                          ? colors.success + '50'
                          : colors.border,
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: selectedFiles[doc.code]
                          ? colors.success + (theme === 'dark' ? '10' : '05')
                          : colors.card,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text }}>
                          {doc.label}
                        </Typography>
                        <Chip
                          label="Optional"
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            borderColor: colors.primary,
                            color: colors.primary
                          }}
                        />
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{ display: "block", mb: 2, color: colors.textSecondary }}
                      >
                        {doc.hint}
                      </Typography>

                      {selectedFiles[doc.code] ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            backgroundColor: colors.success + (theme === 'dark' ? '10' : '05'),
                            padding: '8px 12px',
                            borderRadius: '4px',
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ color: colors.success, fontWeight: 'bold' }}>
                              {uploadingFiles[doc.code] ? '📤 Uploading...' : '✓ Selected'}
                              {uploadProgress[doc.code] && ` (${uploadProgress[doc.code]}%)`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              {selectedFiles[doc.code].name} ({formatFileSize(selectedFiles[doc.code].size)})
                            </Typography>
                            {uploadProgress[doc.code] > 0 && uploadingFiles[doc.code] && (
                              <LinearProgress
                                variant="determinate"
                                value={uploadProgress[doc.code]}
                                sx={{ mt: 1, height: 4, borderRadius: 2 }}
                              />
                            )}
                          </Box>
                          {!uploadingFiles[doc.code] && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveFile(doc.code)}
                              sx={{ color: colors.text }}
                              disabled={isSubmitting}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ) : (
                        <>
                          <input
                            id={`file-input-${doc.code}`}
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.png,.jfif,.heic"
                            onChange={handleFileChange(doc.code, false)}
                            disabled={isSubmitting}
                            style={{
                              width: "100%",
                              padding: "8px",
                              fontSize: "14px",
                              backgroundColor: theme === 'dark' ? colors.card : '#fff',
                              color: colors.text,
                              border: `1px solid ${colors.border}`,
                              borderRadius: "4px",
                              outline: 'none',
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mt: 0.5, color: colors.textSecondary, fontSize: "0.7rem" }}
                          >
                            📄 Allowed: PDF, DOC, DOCX, JPG, PNG, JFIF, HEIC | 📏 Max size: 6 MB
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                ))}
            </>
          )}

        </Grid>

        <div style={{ marginTop: "24px" }}>
          <ContainedButton
            variant="contained"
            onClick={handleBack}
            disabled={isSubmitting || isRejected}
            sx={{
              borderRadius: "1.25rem",
              padding: "10px 25px",
              mr: 2,
              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: theme === 'dark' ? '#0b5ed7' : '#1565c0'
              },
              '&:disabled': {
                backgroundColor: colors.textSecondary,
                color: colors.text,
                cursor: 'not-allowed',
                opacity: 0.6
              }
            }}
          >
            Back
          </ContainedButton>
          <ContainedButton
            type="submit"
            variant="contained"
            disabled={!uploadStatus.valid || isSubmitting}
            sx={{
              borderRadius: "1.25rem",
              padding: "10px 25px",
              backgroundColor: uploadStatus.valid ? colors.primary : colors.textSecondary,
              '&:hover': {
                backgroundColor: uploadStatus.valid
                  ? (theme === 'dark' ? '#0b5ed7' : '#1565c0')
                  : colors.textSecondary
              }
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
                {Object.keys(uploadingFiles).length > 0 ? 'Uploading...' : 'Processing...'}
              </>
            ) : (
              'Next'
            )}
          </ContainedButton>
        </div>
      </form>
    </Paper>
  );
};

export default AccreditedDocumentUpload;